import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, AlertTriangle } from "lucide-react";
import api, { fileUrl } from "@/lib/apiClient";
import SEO from "@/components/SEO";
import ShareButton from "@/components/Shared";

export default function DocumentDetail() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setDoc(null); setNotFound(false);
    api.get(`/documents/${slug}`).then(({ data }) => setDoc(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <div className="font-serif text-7xl text-npst-goldBorder">404</div>
      <h1 className="font-serif text-3xl text-npst-ink mt-4">Representation not found</h1>
      <Link to="/documents" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy">← Back to Documents</Link>
    </div>
  );
  if (!doc) return <div className="py-32 text-center text-npst-muted animate-pulse">Loading…</div>;

  const url = doc.storage_path ? fileUrl(doc.storage_path) : null;
  const isPdf = (doc.ext || "").toLowerCase() === "pdf";
  const rows = [["Reference No.", doc.reference_no], ["Date", doc.doc_date],
    ["Authority / Department", doc.authority], ["Language", doc.language], ["Category", doc.category]];

  return (
    <div className="fade-in max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="document-detail">
      <SEO route={`/documents/${doc.slug || slug}`} title={`${doc.subject || doc.title} — NPST`}
        description={(doc.description || doc.subject || doc.title || "").slice(0, 160)}
        canonical={`${window.location.origin}/documents/${doc.slug || slug}`} />
      <div className="flex items-center justify-between mb-8">
        <Link to="/documents" className="inline-flex items-center gap-2 text-sm text-npst-muted hover:text-npst-navy"><ArrowLeft size={15} /> Document Center</Link>
        <ShareButton title={doc.subject || doc.title} />
      </div>
      <div className="label-eyebrow text-npst-gold mb-2">Representation</div>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-npst-ink leading-snug" data-testid="doc-detail-subject">{doc.subject || doc.title}</h1>
      {doc.needs_review && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1.5 rounded"><AlertTriangle size={13} /> Some details are pending verification</div>
      )}
      <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 bg-white border border-npst-line rounded-md p-5">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="flex flex-col border-b border-npst-line/60 pb-2">
            <span className="text-[11px] uppercase tracking-wider text-npst-gold font-semibold">{k}</span>
            <span className="text-npst-ink font-medium break-words">{v}</span>
          </div>
        ))}
      </div>
      {doc.description && <p className="mt-5 text-npst-inkSoft leading-relaxed">{doc.description}</p>}
      {url && (
        <div className="flex flex-wrap gap-3 mt-6">
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid="doc-detail-view" className="inline-flex items-center gap-2 px-5 py-2.5 bg-npst-navy text-white text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navyDeep"><FileText size={15} /> View PDF</a>
          <a href={url} download data-testid="doc-detail-download" className="inline-flex items-center gap-2 px-5 py-2.5 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-goldLight/40"><Download size={15} /> Download</a>
        </div>
      )}
      {isPdf && url && <iframe title={doc.title} src={url} className="w-full h-[80vh] border border-npst-line rounded-md mt-6" />}
    </div>
  );
}
